import { useMemo, useState } from "react";
import { Repeat, Loader2, CheckCircle2, PlayCircle, Undo2, Pencil, Check, X, Trash2 } from "lucide-react";
import { formatMoney } from "@/components/tx-plan-center/pipelineHelpers";
import MvVisitNotes from "./MvVisitNotes";
import { dentureScopeRequirement } from "@/lib/chairsideHandoffRequirements";

// =====================================================================
// OngoingBundleCard — the on-the-go multi-visit bundle, shown on the
// chairside view and the patient record. Reads a bundle object exactly
// as returned by the get_patient_bundle RPC and renders it; it never
// computes money itself (Build Standard #15 — UI reads from the DB).
//
//   bundle = { plan_id, status, agreed_centavos, paid_centavos,
//              balance_centavos, procedures: [{ item_id, tooth_number,
//              procedure_name, fee_centavos, status, visits_done,
//              recorded_appointment_ids }] }
//   bundle = null  → the patient has no open bundle → render nothing.
//
// Props:
//   onMarkDone(item_id, false)  single-visit "Done" tap → parent renders this
//     visit AND closes the procedure (complete_bundle_procedure).
//   onCompleteItem(item_id)  "Complete and close" — finish a MULTI-visit procedure
//     (complete_bundle_item).
//   onRecordDentureVisit(item_id) records one actual denture visit today.
//   onInstallDenture(item_id) records the denture's final visit and completes
//     the item. The prop keeps the database event name internal; the operator
//     sees the same "Complete and close" contract as every other multi-visit item.
//   onUndoItem(item_id)      reopen a completed procedure line
//     (undo_bundle_item_completion).
//   markingItemId        item_id currently being marked done (spinner)
//
// RCT keeps exactly one action per open procedure — no side-by-side pair:
//   • single-visit (is_multi_visit === false) → "Done" (completes it)
//   • multi-visit  (is_multi_visit === true)  → "Complete and close" (finishes the
//     tooth). The everyday "did work today" for a multi-visit procedure is
//     logged in the "Today's plan" list, NOT here — this card only finishes.
// A grey "Done" (log-a-visit) button used to sit next to "Complete and close" on
// multi-visit rows; that pair is what let a root canal get closed after a
// single visit, so it was removed. is_multi_visit is the CATALOG visit_count
// from get_patient_bundle, never the item's own visit rows. Dentures are the
// recording-layer exception: their row offers the two real events a doctor can
// record, an ordinary visit or final completion. Both write into the same MV.
// =====================================================================

const TERMINAL_ITEM = new Set(["Completed", "Cancelled", "Declined"]);

function treatmentScopeKey(procedure) {
  const arch = String(procedure?.arch || "").toLowerCase();
  if (arch === "upper" || arch === "lower") return `arch:${arch}`;
  if (procedure?.tooth_number) return `tooth:${procedure.tooth_number}`;
  return "general";
}

function treatmentScopeLabel(key) {
  if (key === "arch:upper") return "Upper arch";
  if (key === "arch:lower") return "Lower arch";
  if (key.startsWith("tooth:")) return `#${key.slice("tooth:".length)}`;
  return "General";
}

// The pill LABEL a procedure shows. A "Done" logs a visit but never completes an
// ongoing procedure, so once any work is logged (visits_done > 0) the procedure
// reads "Ongoing" — work started, still going — rather than "Scheduled"
// (untouched) or "Completed". Terminal states keep their real status.
function displayStatus(p) {
  if (TERMINAL_ITEM.has(p.status)) return p.status;
  if (Number(p.visits_done) > 0) return "Ongoing";
  return p.status; // Scheduled / Accepted / Proposed — booked but no visit yet
}

function statusPill(status) {
  if (status === "Completed") return "bg-emerald-50 text-emerald-700";
  if (status === "Cancelled" || status === "Declined") return "bg-slate-100 text-slate-500";
  if (status === "Candidate") return "bg-slate-100 text-slate-600";
  return "bg-violet-50 text-violet-700"; // Ongoing / Scheduled / In Progress / Proposed / Accepted
}

export default function OngoingBundleCard({
  bundle,
  stagedDoneItemIds = null,  // bundle items marked Done in Today's plan (not yet logged)
  onMarkDone,       // log ONE visit — the procedure stays open
  onCompleteItem,   // explicit "Complete and close" — the doctor closes the procedure
  onRecordDentureVisit,
  onInstallDenture,
  onUndoItem,
  onCorrectMistake, // opens a deliberate DB-backed correction gate for one item or the whole MV
  onEditPrice,      // re-price one item — only while the bundle is first-staged
  onRequestFinish,
  onCancelFinish,
  finishRequesting = false,
  markingItemId = null,
  completingItemId = null,
  undoingItemId = null,
  notes = null,
  currentAppointmentId = null,
  onSaveNote,
  onAddNoteAddendum,
  showFinishControls = true,
  allowDentureActions = true,
}) {
  const byTooth = useMemo(() => {
    const groups = new Map();
    for (const p of bundle?.procedures ?? []) {
      const key = treatmentScopeKey(p);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    }
    return Array.from(groups.entries());
  }, [bundle]);

  const [editingItemId, setEditingItemId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);
  const canEditPrice = Boolean(bundle?.price_editable) && typeof onEditPrice === "function";
  const hasOpenDentureItems = (bundle?.procedures ?? []).some((procedure) => (
    ["partial_denture", "full_denture"].includes(procedure.multi_tooth_kind)
    && !TERMINAL_ITEM.has(procedure.status)
  ));

  async function submitPrice(itemId) {
    const pesos = Number(editValue);
    if (!Number.isFinite(pesos) || pesos < 0) return;
    setSavingPrice(true);
    const ok = await onEditPrice(itemId, Math.round(pesos * 100));
    setSavingPrice(false);
    if (ok !== false) setEditingItemId(null);
  }

  if (!bundle) return null;

  return (
    <div
      data-testid="ongoing-bundle-card"
      data-tutorial-id="ongoing-bundle-card"
      className={`h-full rounded-2xl border overflow-hidden transition-colors ${
        bundle.finish_requested
          ? "border-slate-200 bg-slate-50 opacity-80"
          : "border-violet-200 bg-white"
      }`}
    >
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Repeat className="w-4 h-4 text-violet-600 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              Ongoing treatment
              {bundle.mv_code ? (
                <span className="ml-2 text-[11px] text-slate-400 font-normal">{bundle.mv_code}</span>
              ) : null}
            </p>
            <p className="text-[12px] text-slate-500">
              {showFinishControls
                ? "Record each visit. Complete and close when no more visits are needed."
                : "Record each visit. Complete an item only when it needs no more visits."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {typeof onCorrectMistake === "function" ? (
            <button
              type="button"
              data-testid="bundle-remove-treatment"
              onClick={() => onCorrectMistake(null)}
              className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-slate-400 hover:bg-rose-50 hover:text-rose-700"
              title="Remove this treatment only if it was entered by mistake"
            >
              <Trash2 className="h-3 w-3" />
              Remove mistake
            </button>
          ) : null}
          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${
            bundle.finish_requested ? "bg-emerald-50 text-emerald-700" : "bg-violet-50 text-violet-700"
          }`}>
            {bundle.finish_requested ? "Done" : "Ongoing"}
          </span>
        </div>
      </div>

      <div className="px-4 py-3 grid grid-cols-3 gap-2">
        <Metric label="Agreed total" value={formatMoney(bundle.agreed_centavos)} />
        <Metric label="Paid" value={formatMoney(bundle.paid_centavos)} tone="text-emerald-600" />
        <Metric label="Balance left" value={formatMoney(bundle.balance_centavos)} />
      </div>

      <div className="px-4 pb-3 space-y-3">
        {byTooth.map(([scope, procs]) => (
          <div key={scope}>
            <p className="text-[10px] font-semibold tracking-wide text-slate-400 uppercase mb-1">
              {treatmentScopeLabel(scope)}
            </p>
            <div className="space-y-1.5">
              {procs.map((p) => {
                const done = p.status === "Completed";
                const canMark = !TERMINAL_ITEM.has(p.status);
                const isDenture = ["partial_denture", "full_denture"].includes(p.multi_tooth_kind);
                const recordedOnCurrentAppointment = isDenture
                  && Boolean(currentAppointmentId)
                  && (p.recorded_appointment_ids || []).some(
                    (appointmentId) => String(appointmentId) === String(currentAppointmentId),
                  );
                const missingDentureScope = dentureScopeRequirement(p);
                const canUndo = done
                  && typeof onUndoItem === "function"
                  && !["AR", "Completed"].includes(bundle.status);
                const undoing = undoingItemId === p.item_id;
                // Marked Done in Today's plan this visit but not yet logged
                // (logs at checkout) → show "Ongoing" now, not "Scheduled".
                const stagedDone = !!(stagedDoneItemIds && stagedDoneItemIds.has(p.item_id));
                const effStatus = (stagedDone && Number(p.visits_done) === 0 && !TERMINAL_ITEM.has(p.status))
                  ? "Ongoing"
                  : displayStatus(p);
                return (
                  <div
                    key={p.item_id}
                    className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-50 last:border-0"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {done ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      ) : (
                        <PlayCircle className="w-4 h-4 text-violet-500 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{p.procedure_name}</p>
                        {editingItemId === p.item_id ? (
                          <div className="mt-0.5 flex items-center gap-1">
                            <span className="text-[11px] text-slate-500">₱</span>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              autoFocus
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") submitPrice(p.item_id);
                                if (e.key === "Escape") setEditingItemId(null);
                              }}
                              data-testid={`bundle-price-input-${p.item_id}`}
                              className="w-24 h-7 rounded border border-slate-300 px-2 text-[12px] outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-200"
                            />
                            <button
                              type="button"
                              onClick={() => submitPrice(p.item_id)}
                              disabled={savingPrice}
                              data-testid={`bundle-price-save-${p.item_id}`}
                              aria-label="Save price"
                              className="w-7 h-7 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 inline-flex items-center justify-center"
                            >
                              {savingPrice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingItemId(null)}
                              aria-label="Cancel"
                              className="w-7 h-7 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <p className="text-[11px] text-slate-500 flex items-center gap-1">
                              {formatMoney(p.fee_centavos)}
                              {canEditPrice ? (
                                <button
                                  type="button"
                                  onClick={() => { setEditingItemId(p.item_id); setEditValue(String((p.fee_centavos ?? 0) / 100)); }}
                                  data-testid={`bundle-price-edit-${p.item_id}`}
                                  aria-label={`Edit price of ${p.procedure_name}`}
                                  title="Edit price"
                                  className="text-slate-400 hover:text-violet-600"
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                              ) : null}
                              {p.visits_done > 0 ? ` · ${p.visits_done} visit${p.visits_done === 1 ? "" : "s"} done` : ""}
                            </p>
                            {isDenture && p.billing ? (
                              <p
                                className="mt-0.5 text-[11px] text-slate-600"
                                data-testid={`bundle-denture-billing-${p.item_id}`}
                              >
                                Paid {formatMoney(p.billing.paid_centavos)} · Balance {formatMoney(p.billing.balance_centavos)}
                                {Number(p.billing.suggested_next_centavos || 0) > 0
                                  ? ` · Next ${formatMoney(p.billing.suggested_next_centavos)}`
                                  : ""}
                              </p>
                            ) : null}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusPill(effStatus)}`}>
                        {effStatus}
                      </span>
                      {typeof onCorrectMistake === "function" ? (
                        <button
                          type="button"
                          data-testid={`bundle-remove-item-${p.item_id}`}
                          aria-label={`Remove mistaken item: ${p.procedure_name}`}
                          title="Remove this item only if it was entered by mistake"
                          onClick={() => onCorrectMistake(p)}
                          className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-slate-400 hover:bg-rose-50 hover:text-rose-700"
                        >
                          <Trash2 className="h-3 w-3" />
                          Mistake
                        </button>
                      ) : null}
                      {canUndo ? (
                        <button
                          type="button"
                          data-testid={`bundle-undo-${p.item_id}`}
                          aria-label={`Undo ${p.procedure_name} completion`}
                          title="Undo completion"
                          onClick={() => onUndoItem?.(p.item_id)}
                          disabled={undoing}
                          className="w-7 h-7 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-40 inline-flex items-center justify-center"
                        >
                          {undoing ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Undo2 className="w-3.5 h-3.5" />
                          )}
                        </button>
                      ) : null}
                      {canMark ? (
                        <>
                          {/* SINGLE-visit procedure: one completion verb, "Done"
                              (renders this visit AND closes the procedure). It's
                              the only button on the row, so there is nothing to
                              confuse it with. */}
                          {!p.is_multi_visit && !isDenture ? (
                            <button
                              type="button"
                              data-testid={`bundle-done-${p.item_id}`}
                              onClick={() => onMarkDone?.(p.item_id, false)}
                              disabled={markingItemId === p.item_id || completingItemId === p.item_id}
                              className="text-[11px] font-semibold px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 inline-flex items-center gap-1"
                            >
                              {markingItemId === p.item_id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <CheckCircle2 className="w-3 h-3" />
                              )}
                              Done
                            </button>
                          ) : null}
                          {/* MULTI-visit procedure: the ONLY action here is
                              "Complete and close" (finish this tooth). Logging a day's
                              visit happens in the "Today's plan" list, so there
                              is deliberately NO "Done" button beside this one —
                              that side-by-side pair is what let a root canal get
                              closed after a single visit. */}
                          {p.is_multi_visit && !isDenture ? (
                            <button
                              type="button"
                              data-testid={`bundle-complete-${p.item_id}`}
                              data-tutorial-id={p.tutorial_complete_id || undefined}
                              onClick={() => onCompleteItem?.(p.item_id)}
                              disabled={completingItemId === p.item_id || markingItemId === p.item_id}
                              className="text-[11px] font-semibold px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 inline-flex items-center gap-1"
                            >
                              {completingItemId === p.item_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                              Complete and close
                            </button>
                          ) : null}
                          {isDenture && allowDentureActions ? (
                            <>
                              <button
                                type="button"
                                data-testid={`bundle-record-denture-${p.item_id}`}
                                onClick={() => onRecordDentureVisit?.(p.item_id, p)}
                                disabled={recordedOnCurrentAppointment || markingItemId === p.item_id || completingItemId === p.item_id}
                                className={`text-[11px] font-semibold px-2 py-1 rounded border inline-flex items-center gap-1 disabled:cursor-default ${
                                  recordedOnCurrentAppointment
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-40"
                                }`}
                              >
                                {markingItemId === p.item_id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : recordedOnCurrentAppointment ? (
                                  <CheckCircle2 className="w-3 h-3" />
                                ) : (
                                  <PlayCircle className="w-3 h-3" />
                                )}
                                {recordedOnCurrentAppointment
                                  ? "Recorded today"
                                  : (missingDentureScope?.actionLabel || "Record visit")}
                              </button>
                              {p.arch ? (
                                <button
                                  type="button"
                                  data-testid={`bundle-install-denture-${p.item_id}`}
                                  onClick={() => onInstallDenture?.(p.item_id)}
                                  disabled={completingItemId === p.item_id || markingItemId === p.item_id}
                                  aria-label={`Complete and close ${p.procedure_name}`}
                                  title="Completes and closes this item. No more visits will be needed."
                                  className="text-[11px] font-semibold px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 inline-flex items-center gap-1"
                                >
                                  {completingItemId === p.item_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                  Complete and close
                                </button>
                              ) : null}
                            </>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {notes ? (
        <MvVisitNotes
          notes={notes}
          appointmentId={currentAppointmentId}
          onSave={(payload) => onSaveNote?.(payload)}
          onAddendum={(noteId, text) => onAddNoteAddendum?.(noteId, text)}
        />
      ) : null}

      {showFinishControls && bundle.finish_requested ? (
        // Doctor already flagged it — waiting for front desk to bill + close.
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <span className="text-[12px] font-medium text-emerald-700 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" />
            Done — front desk will bill this at checkout
          </span>
          <button
            type="button"
            data-testid="bundle-cancel-finish"
            onClick={() => onCancelFinish?.()}
            disabled={finishRequesting}
            className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            Undo
          </button>
        </div>
      ) : showFinishControls && hasOpenDentureItems ? (
        <div
          className="px-4 py-3 border-t border-violet-100 bg-violet-50/50"
          data-testid="bundle-finish-denture-guard"
        >
          <p className="text-[12px] font-medium text-violet-800">
            Complete and close every item when it needs no more visits. This MV stays open until every item is complete.
          </p>
        </div>
      ) : showFinishControls ? (
        // "Tapos na, bill it" — flags the treatment done for the front desk.
        <div className="px-4 py-3 border-t border-slate-100">
          <button
            type="button"
            data-testid="bundle-finish"
            data-tutorial-id="bundle-finish"
            onClick={() => onRequestFinish?.()}
            disabled={finishRequesting}
            className="w-full px-3 py-2.5 text-sm font-semibold border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
          >
            {finishRequesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Finish and close treatment
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Metric({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className={`text-base font-semibold ${tone}`}>{value}</p>
    </div>
  );
}
